import { buildQueryString } from "../lib/query";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  auth?: boolean;
}

type UnauthorizedHandler = () => void;

export class ApiClient {
  private csrfToken = "";
  private unauthorizedHandler: UnauthorizedHandler | null = null;

  constructor(private readonly baseUrl: string) {}

  setCsrfToken(token: string): void {
    this.csrfToken = token;
  }

  clearCsrfToken(): void {
    this.csrfToken = "";
  }

  setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
    this.unauthorizedHandler = handler;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const {
      body,
      query,
      auth = true,
      headers: customHeaders,
      ...requestInit
    } = options;
    const url = `${this.baseUrl}${path}${buildQueryString(query ?? {})}`;
    const headers = new Headers(customHeaders);
    const method = (requestInit.method ?? "GET").toUpperCase();
    if (auth && this.csrfToken && !["GET", "HEAD", "OPTIONS"].includes(method)) {
      headers.set("X-CSRF-Token", this.csrfToken);
    }

    let requestBody: BodyInit | undefined;
    if (body instanceof FormData || body instanceof Blob || typeof body === "string") {
      requestBody = body;
    } else if (body !== undefined) {
      headers.set("Content-Type", "application/json");
      requestBody = JSON.stringify(body);
    }

    const response = await fetch(url, {
      ...requestInit,
      headers,
      body: requestBody,
      credentials: "include",
    });
    if (response.status === 401 && auth && this.unauthorizedHandler) {
      this.unauthorizedHandler();
    }
    if (!response.ok) throw await this.toError(response);
    if (response.status === 204) return undefined as T;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) return (await response.json()) as T;
    return (await response.text()) as T;
  }

  async download(path: string): Promise<{ blob: Blob; filename: string | null }> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: "include",
    });
    if (response.status === 401 && this.unauthorizedHandler) {
      this.unauthorizedHandler();
    }
    if (!response.ok) throw await this.toError(response);
    const disposition = response.headers.get("content-disposition");
    const match = disposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
    return {
      blob: await response.blob(),
      filename: match ? decodeURIComponent(match[1] ?? "") : null,
    };
  }

  private async toError(response: Response): Promise<ApiError> {
    const contentType = response.headers.get("content-type") ?? "";
    let details: unknown = null;
    try {
      details = contentType.includes("application/json")
        ? await response.json()
        : await response.text();
    } catch {
      details = null;
    }
    const message = extractErrorMessage(details) || `Ошибка API: ${response.status}`;
    return new ApiError(message, response.status, details);
  }
}

function extractErrorMessage(details: unknown): string {
  if (typeof details === "string") return details;
  if (!details || typeof details !== "object") return "";
  const value = (details as Record<string, unknown>).detail;
  if (typeof value === "string") return value;
  if (value !== undefined) return JSON.stringify(value);
  return "";
}
